import { toast } from "react-toastify";
import store from "../Store/store";
import { findEmbeddableEnvironmentInfo, sendApiRequest } from "./requestConfig";
import * as XLSX from "xlsx";
import { checkTodayDate, checkTodayDateWithFormat, dateInCorrectTimezone, reformatDateToUserFormat } from "./utils";

export const fetchDownloadableData = async (dashboardVariables, numberRoot, embeddableId) => {
    const currentState = store.getState().userSelections;
    // endpoint to send the request to
    const endpointUrl = "api/embeddable/data/capacity-check";
    // embeddable environment info
    const embeddableEnv = findEmbeddableEnvironmentInfo();
    // prepare the start/end dates in the format expected by the backend
    const startDate = reformatDateToUserFormat(currentState.advertising_capacity_selected_dates.from, "YYYY-MM-DD");
    const endDate = reformatDateToUserFormat(currentState.advertising_capacity_selected_dates.to, "YYYY-MM-DD");
    // end date +1 day for BE API (exclusive end date convention, same as embeddable)
    // const endDatePlusOne = new Date(currentState.advertising_capacity_selected_dates.to);
    // endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
    const endDateForApi = endDate;// reformatDateToUserFormat(endDatePlusOne, "YYYY-MM-DD");

    const listingRequestPayload = {
        "embeddable_id": embeddableId,
        "number_root": numberRoot.toString(),
        "environment": embeddableEnv.environment,
        "saved_version": embeddableEnv.savedVersion,
        "stores": dashboardVariables.stores,
        "start_date": startDate,
        "end_date": endDateForApi,
    };
    // send the request
    const sendRequest = await sendApiRequest(endpointUrl, listingRequestPayload, "POST");
    if (typeof sendRequest.success !== "undefined" && sendRequest.success === true) {
        const apiResponse = sendRequest.response;

        const stores = currentState.stores_list.filter(store => dashboardVariables.stores.includes(store.id)).map(store => {
            return {
                storeName: store.name,
                streetAndNo: store.address,
                zipCode: store.zipCode,
                city: store.city,
                region: store.region.name,
                owner: store.company.name,
            }
        });

        exportRemainingCapacityExcel({
            apiResponse,
            stores,
            startDate: startDate,
            endDate: endDate,
        });
    } else {
        toast("Sorry, something went wrong", { type: "error" });
    }
};

export function exportRemainingCapacityExcel({
    apiResponse,
    stores,
    startDate,
    endDate
}) {
    const workbook = XLSX.utils.book_new();
    const exportDate = checkTodayDate();

    const weeklyRows = Object.entries(apiResponse.weekly_impressions || {})
        .sort(([weekA], [weekB]) => Number(weekA) - Number(weekB))
        .map(([calendarWeek, values]) => [
            Number(calendarWeek),
            values.weeklyForecastImpressions,
            values.weeklyBookedImpressions,
            values.weeklyRemainingImpressions,
            values.weeklyUtilizationRate + "%",
        ]);

    const remainingCapacityData = [
        ["Capacity Planning"],
        [
            "(The first and last calendar weeks only contain the weekdays included in the selected date range.",
        ],
        [
            "The results represent a snapshot of the current situation, as scheduled campaigns and forecast impressions may change over time.",
        ],
        [
            "Forecast data is based on historical data patterns and may not always be perfectly accurate.)",
        ],
        [],
        ["Export date", exportDate],
        [
            "Timeframe",
            `${dateInCorrectTimezone(startDate)} - ${dateInCorrectTimezone(endDate)}`,
        ],
        [
            "Stores",
            `${stores.length} (see Stores sheet for detailed list of stores)`,
        ],
        [],
        [
            "Total Forecast Impressions",
            apiResponse.total_forecast_impressions,
        ],
        [
            "Total Booked Impressions",
            apiResponse.total_booked_impressions,
        ],
        [
            "Total Remaining Impressions",
            apiResponse.total_remaining_impressions,
        ],
        ["Utilization Rate",
            apiResponse.total_utilization_rate + "%"
        ],
        [],
        [
            "Calendar Week",
            "Forecast Impressions",
            "Booked Impressions",
            "Remaining Impressions",
            "Utilization Rate",
        ],
        ...weeklyRows,
    ];

    const remainingCapacitySheet = XLSX.utils.aoa_to_sheet(
        remainingCapacityData
    );

    // !cols is a special property used by xlsx to define column widths
    // wch = “width in characters”
    remainingCapacitySheet["!cols"] = [
        { wch: 22 },
        { wch: 22 },
        { wch: 22 },
        { wch: 24 },
        { wch: 18 },
    ];

    if (remainingCapacitySheet["B13"]) {
        // t = "n" → tells Excel this is a number
        // z = "0%" → tells Excel to display it as a percentage
        // remainingCapacitySheet["B13"].t = "n";
        // remainingCapacitySheet["B13"].z = "0%";
    }

    for (let row = 16; row < 16 + weeklyRows.length; row++) {
        const utilCellAddress = `E${row}`;
        if (remainingCapacitySheet[utilCellAddress]) {
            // remainingCapacitySheet[utilCellAddress].t = "n";
            // remainingCapacitySheet[utilCellAddress].z = "0%";
        }
    }

    XLSX.utils.book_append_sheet(
        workbook,
        remainingCapacitySheet,
        "Capacity Planning"
    );

    const storesData = [
        ["#", "Store Name", "Street & No.", "ZIP Code", "City", "Region", "Owner"],
        ...stores.sort((a, b) => a.storeName.localeCompare(b.storeName)).map((store, index) => [
            index + 1,
            store.storeName,
            store.streetAndNo,
            store.zipCode,
            store.city,
            store.region,
            store.owner,
        ]), // Sort stores alphabetically by store name
    ];

    const storesSheet = XLSX.utils.aoa_to_sheet(storesData);

    storesSheet["!cols"] = [
        { wch: 6 },
        { wch: 24 },
        { wch: 24 },
        { wch: 12 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
    ];

    XLSX.utils.book_append_sheet(workbook, storesSheet, "Stores");
    const fileName = checkTodayDateWithFormat("YYYYMMDD") + "_Capacity_Planning.xlsx";
    XLSX.writeFile(workbook, fileName);
}

